/**
 * API админки платформы: обзор, срез по бизнесу, ростер админов и выдача Pro.
 *
 * Поверх ТОГО ЖЕ `HttpTransport`, что и остальное приложение (см. `cabinet.ts`): заголовок
 * авторизации и разбор ошибок одни на всё. `auth: true` — доступ закрыт подписью и сверкой
 * `tg_id` с админами платформы на бэкенде; не-админ получает 403.
 *
 * Формы DTO списаны с `toOverviewDto` и роутера в `packages/backend/src/routes/admin.ts` —
 * единственного места, где бэкенд решает, что уходит наружу.
 */

import type { HttpTransport } from './http';
import type { StatsDto, StatsPeriod } from './cabinet';

export type AdminPlan = 'pro' | 'trial' | 'free';

export interface AdminTotalsDto {
  businesses: number;
  published: number;
  clients: number;
  appointments: number;
  completed: number;
  noShow: number;
  pro: number;
  trial: number;
  free: number;
}

export interface AdminFunnelRowDto {
  /** Метка `src_…` из ссылки `t.me/<бот>?start=src_…`. */
  source: string;
  started: number;
  owners: number;
  businesses: number;
  withBooking: number;
}

export interface AdminBusinessRowDto {
  id: string;
  name: string;
  city: string | null;
  type: string;
  isPublished: boolean;
  plan: AdminPlan;
  clients: number;
  appointments: number;
  noShow: number;
  /** ISO 8601 в UTC; `null` — записей ещё нет. */
  lastActivityAt: string | null;
  /** Оплаченный Pro до (ISO 8601); `null` — не оплачен. Для показа остатка дней в админке. */
  proUntil: string | null;
  /** Конец триала (ISO 8601); `null` — без триала. */
  trialEndsAt: string | null;
}

/** Точка графика активности: один день в поясе Asia/Tashkent (считает бэкенд). */
export interface AdminActivityPointDto {
  /** `YYYY-MM-DD` в поясе Asia/Tashkent. */
  day: string;
  appointments: number;
  registrations: number;
  pro: number;
}

export type AdminEventKind = 'business' | 'appointment' | 'pro';

/** Событие ленты платформы. `at` — ISO 8601 в UTC. */
export interface AdminEventDto {
  kind: AdminEventKind;
  org: string;
  at: string;
}

export interface AdminOverviewDto {
  totals: AdminTotalsDto;
  funnel: AdminFunnelRowDto[];
  businesses: AdminBusinessRowDto[];
  /** Дневной ряд за 30 дней для графика активности. */
  activity: AdminActivityPointDto[];
  /** Последние события платформы (до 15). */
  events: AdminEventDto[];
}

/** Строка ростера админов. `tgId` — строкой: bigint за пределами точного диапазона double. */
export interface AdminRosterRowDto {
  tgId: string;
  note: string;
  /** Владелец из `ADMIN_TG_ID` — его нельзя удалить. */
  isOwner: boolean;
  /** ISO 8601; `null` у владельца-бутстрапа (строки в базе для него нет). */
  createdAt: string | null;
}

/**
 * Что делаем с Pro организации: ровно один вариант. Форма повторяет `parseGrant` в роутере —
 * бэкенд на пустой или двойственный грант отвечает `err.validation`, а не выбирает молча.
 */
export type ProGrantInput = { months: number } | { forever: true } | { revoke: true };

export interface ProGrantResultDto {
  /** ISO 8601 или `null`, если Pro снят. */
  proUntil: string | null;
  isPro: boolean;
}

export interface AdminApiClient {
  overview(): Promise<AdminOverviewDto>;
  /** Срез по времени конкретного бизнеса — та же форма, что `cabinet.getStats`, но по любому. */
  businessStats(businessId: string, period: StatsPeriod): Promise<StatsDto>;
  /** Ростер админов платформы (владелец + строки `platform_admins`). */
  listAdmins(): Promise<AdminRosterRowDto[]>;
  /** Добавить админа по `tg_id`. `note` — пометка «кто это», до 200 символов. */
  addAdmin(tgId: string, note?: string): Promise<void>;
  /** Снять админа. Владельца-бутстрапа снять нельзя (бэкенд не отдаёт его в удаляемых). */
  removeAdmin(tgId: string): Promise<void>;
  /** Ручная выдача/продление/снятие Pro у организации (без оплаты). */
  grantPro(businessId: string, grant: ProGrantInput): Promise<ProGrantResultDto>;
}

export function createAdminApi(transport: HttpTransport): AdminApiClient {
  return {
    overview: () =>
      transport.request<AdminOverviewDto>({ method: 'GET', path: '/admin/overview', auth: true }),
    businessStats: (businessId, period) =>
      transport.request<StatsDto>({
        method: 'GET',
        path: `/admin/businesses/${encodeURIComponent(businessId)}/stats`,
        query: { period },
        auth: true,
      }),
    // Бэкенд оборачивает список в `{ admins }` — разворачиваем здесь, экрану нужен массив.
    listAdmins: async () =>
      (
        await transport.request<{ admins: AdminRosterRowDto[] }>({
          method: 'GET',
          path: '/admin/admins',
          auth: true,
        })
      ).admins,
    // Ответ `{ ok: true }` не нужен вызывающему: успех — отсутствие отказа. Но это не 204,
    // поэтому `empty` не ставим — тело есть, транспорт его разберёт и мы его отбросим.
    addAdmin: async (tgId, note) => {
      await transport.request<{ ok: boolean }>({
        method: 'POST',
        path: '/admin/admins',
        body: { tgId, note: note ?? '' },
        auth: true,
      });
    },
    removeAdmin: async (tgId) => {
      await transport.request<{ ok: boolean }>({
        method: 'DELETE',
        path: `/admin/admins/${encodeURIComponent(tgId)}`,
        auth: true,
      });
    },
    grantPro: (businessId, grant) =>
      transport.request<ProGrantResultDto>({
        method: 'POST',
        path: `/admin/businesses/${encodeURIComponent(businessId)}/pro`,
        body: grant,
        auth: true,
      }),
  };
}
